import { storage } from '@/lib/firebase'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'

/**
 * Upload a flash image to Firebase Storage
 * @param file - The image file to upload
 * @param displayName - The artist's display name (Instagram username)
 * @param flashId - Unique identifier for the flash
 * @returns Promise<string> - The download URL of the uploaded image
 */
export async function uploadFlashImage(
  file: File,
  displayName: string,
  flashId: string
): Promise<string> {
  try {
    // Create a reference to the file location
    const fileName = `${flashId}_${Date.now()}.${file.name.split('.').pop()}`
    const storageRef = ref(storage, `onboardings/${displayName}/${fileName}`)
    
    // Upload the file
    const snapshot = await uploadBytes(storageRef, file)
    
    // Get the download URL
    const downloadURL = await getDownloadURL(snapshot.ref)
    
    return downloadURL
  } catch (error) {
    console.error('Error uploading flash image:', error)
    throw error instanceof Error ? error : new Error('Failed to upload flash image')
  }
}

/**
 * Convert a data URL to a File object
 * @param dataUrl - The data URL (e.g., from FileReader.readAsDataURL)
 * @param fileName - The desired file name
 * @returns File object
 */
export function dataUrlToFile(dataUrl: string, fileName: string): File {
  const arr = dataUrl.split(',')
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg'
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  
  return new File([u8arr], fileName, { type: mime })
}

/**
 * Upload flash image from data URL
 * @param dataUrl - The data URL of the image
 * @param displayName - The artist's display name
 * @param flashId - Unique identifier for the flash
 * @returns Promise<string> - The download URL of the uploaded image
 */
export async function uploadFlashFromDataUrl(
  dataUrl: string,
  displayName: string,
  flashId: string
): Promise<string> {
  const file = dataUrlToFile(dataUrl, `flash_${flashId}.jpg`)
  return uploadFlashImage(file, displayName, flashId)
}
